import React, { useEffect, useState, useCallback, useMemo } from 'react';
import { createPortal } from 'react-dom';
import { useNavigate } from 'react-router-dom';
import { Command } from 'cmdk';
import axios from 'axios';
import {
  LayoutDashboard,
  Megaphone,
  PanelTop,
  Gift,
  Globe2,
  Route,
  FlaskConical,
  ShieldCheck,
  BarChart3,
  Workflow,
  Plug,
  ServerCog,
  KeyRound,
  Users2,
  CreditCard,
  Settings as SettingsIcon,
  ArrowRight,
  Search,
  Plus,
  ExternalLink,
  Cloud,
  Layers,
  ListChecks,
  ClipboardList,
  Cookie,
  Store,
  BookOpen,
  AlertTriangle,
  Activity,
  Lock,
  CircleUserRound,
  Bell,
  Palette,
  Bug,
} from 'lucide-react';
import { useT } from '../i18n';
import { API, useAuth } from '../App';
import { visibleNavItems } from '../navVisibility';
import { useSelfHosted } from '../lib/instanceLicence';
import { BACKEND_URL } from '../runtimeConfig';

// Mirrors the sidebar Navbar.jsx — keep these in sync. The order here
// is what shows up in the palette; group titles are looked up via t().
const NAV_ENTRIES = [
  { id: 'nav-dashboard', label: 'Dashboard', path: '/dashboard', icon: LayoutDashboard, group: 'overview' },
  { id: 'nav-analytics', label: 'Analytics', path: '/analytics', icon: BarChart3, group: 'overview' },
  { id: 'nav-clicks-log', label: 'Clicks log', path: '/clicks/log', icon: ListChecks, group: 'overview' },
  { id: 'nav-reports', label: 'Reports', path: '/reports', icon: BarChart3, group: 'overview' },
  { id: 'nav-sub-id-reports', label: 'Sub-ID reports', path: '/reports/sub-ids', icon: ClipboardList, group: 'overview' },
  { id: 'nav-compare', label: 'Campaign comparison', path: '/campaigns/compare', icon: ArrowRight, group: 'campaigns' },

  { id: 'nav-campaigns', label: 'Campaigns', path: '/campaigns', icon: Megaphone, group: 'campaigns' },
  { id: 'nav-landing-pages', label: 'Landing pages', path: '/landing-pages', icon: PanelTop, group: 'campaigns' },
  { id: 'nav-pre-landings', label: 'Pre-landings', path: '/pre-landings', icon: Layers, group: 'campaigns' },
  { id: 'nav-offers', label: 'Offers', path: '/offers', icon: Gift, group: 'campaigns' },
  { id: 'nav-white-pages', label: 'White pages', path: '/white-pages', icon: ShieldCheck, group: 'campaigns' },
  { id: 'nav-affiliate-networks', label: 'Affiliate networks', path: '/affiliate-networks', icon: Plug, group: 'campaigns' },
  { id: 'nav-leads', label: 'Leads', path: '/leads', icon: Users2, group: 'campaigns' },
  { id: 'nav-traffic-sources', label: 'Traffic sources', path: '/traffic-sources', icon: Globe2, group: 'campaigns' },
  { id: 'nav-traffic', label: 'Fallback rules', path: '/traffic', icon: Route, group: 'campaigns' },
  { id: 'nav-ab-testing', label: 'A/B testing', path: '/ab-testing', icon: FlaskConical, group: 'campaigns' },

  { id: 'nav-anti-fraud', label: 'Anti-fraud', path: '/anti-fraud', icon: ShieldCheck, group: 'tools' },
  { id: 'nav-automation', label: 'Automation', path: '/automation', icon: Workflow, group: 'tools' },
  { id: 'nav-conversion-api', label: 'Conversion API', path: '/conversion-api', icon: ServerCog, group: 'tools' },
  { id: 'nav-domains', label: 'Custom domains', path: '/domains', icon: Cloud, group: 'tools' },
  { id: 'nav-integrations', label: 'Integrations', path: '/integrations', icon: Plug, group: 'tools' },
  { id: 'nav-marketplace', label: 'Marketplace', path: '/marketplace', icon: Store, group: 'tools' },
  { id: 'nav-consent-banner', label: 'Consent banner', path: '/consent-banner', icon: Cookie, group: 'tools' },

  { id: 'nav-workspace', label: 'Workspace', path: '/workspace', icon: Users2, group: 'workspace' },
  { id: 'nav-ad-accounts', label: 'Ad accounts', path: '/ad-account-connections', icon: CircleUserRound, group: 'workspace' },
  { id: 'nav-api-keys', label: 'API keys', path: '/api-keys', icon: KeyRound, group: 'workspace' },
  { id: 'nav-security', label: 'Security', path: '/settings/security', icon: Lock, group: 'workspace' },

  { id: 'nav-audit-log', label: 'Audit log', path: '/admin/audit', icon: ClipboardList, group: 'workspace' },

  // Operator-only entries — filtered out for non-platform-admins in
  // navEntriesByGroup (mirrors the sidebar's visibleNavItems gating).
  { id: 'nav-platform-admin', label: 'Platform admin', path: '/admin/platform', icon: ShieldCheck, group: 'admin' },
  { id: 'nav-capi-dlq', label: 'CAPI DLQ', path: '/admin/capi-dlq', icon: AlertTriangle, group: 'tools' },
  { id: 'nav-error-reports', label: 'Error reports', path: '/admin/errors', icon: Bug, group: 'admin' },
  { id: 'nav-system-health', label: 'System health', path: '/admin/health', icon: Activity, group: 'admin' },

  { id: 'nav-docs', label: 'Documentation', path: '/docs', icon: BookOpen, group: 'account' },
  { id: 'nav-billing', label: 'Billing', path: '/billing', icon: CreditCard, group: 'account' },
  { id: 'nav-branding', label: 'Branding', path: '/workspace/branding', icon: Palette, group: 'account' },
  { id: 'nav-notifications', label: 'Notifications', path: '/notifications/preferences', icon: Bell, group: 'account' },
  { id: 'nav-settings', label: 'Settings', path: '/settings', icon: SettingsIcon, group: 'account' },
];

const NAV_GROUPS = [
  { key: 'overview', label: 'Overview' },
  { key: 'campaigns', label: 'Campaigns' },
  { key: 'tools', label: 'Tools' },
  { key: 'workspace', label: 'Workspace' },
  { key: 'admin', label: 'Admin' },
  { key: 'account', label: 'Account' },
];

const EXTERNAL_LINKS = [
  { id: 'ext-api-docs', label: 'API docs', icon: BookOpen },
];

export default function XpnsrCommandPalette() {
  const navigate = useNavigate();
  const { t } = useT();
  const { user } = useAuth();
  const selfHosted = useSelfHosted();
  const [open, setOpen] = useState(false);
  const [query, setQuery] = useState('');
  const [campaigns, setCampaigns] = useState([]);
  const [landingPages, setLandingPages] = useState([]);
  const [offers, setOffers] = useState([]);
  const [trafficSources, setTrafficSources] = useState([]);
  const [affiliateNetworks, setAffiliateNetworks] = useState([]);
  const [domains, setDomains] = useState([]);
  const [hasFetched, setHasFetched] = useState(false);

  const close = useCallback(() => {
    setOpen(false);
    setQuery('');
  }, []);

  // Toggle on Cmd/Ctrl+K, plus custom event fired by the topbar button
  useEffect(() => {
    const onKey = (e) => {
      if ((e.metaKey || e.ctrlKey) && (e.code === 'KeyK' || (e.key || '').toLowerCase() === 'k')) {
        e.preventDefault();
        setOpen((prev) => !prev);
      }
    };
    const onOpenEvent = () => setOpen(true);
    window.addEventListener('keydown', onKey);
    window.addEventListener('xpnsr:open-cmdk', onOpenEvent);
    return () => {
      window.removeEventListener('keydown', onKey);
      window.removeEventListener('xpnsr:open-cmdk', onOpenEvent);
    };
  }, []);

  // Lazy-load search corpus once when palette first opens
  useEffect(() => {
    if (!open || hasFetched) return;
    let cancelled = false;
    const load = async () => {
      try {
        const [cRes, lRes, oRes, sRes, nRes, dRes] = await Promise.allSettled([
          axios.get(`${API}/campaigns`),
          axios.get(`${API}/landing-pages`),
          axios.get(`${API}/offers`),
          axios.get(`${API}/traffic-sources`),
          axios.get(`${API}/affiliate-networks`),
          axios.get(`${API}/domains`),
        ]);
        if (cancelled) return;
        if (cRes.status === 'fulfilled') setCampaigns(Array.isArray(cRes.value.data) ? cRes.value.data : []);
        if (lRes.status === 'fulfilled') setLandingPages(Array.isArray(lRes.value.data) ? lRes.value.data : []);
        if (oRes.status === 'fulfilled') setOffers(Array.isArray(oRes.value.data) ? oRes.value.data : []);
        if (sRes.status === 'fulfilled') setTrafficSources(Array.isArray(sRes.value.data) ? sRes.value.data : []);
        if (nRes.status === 'fulfilled') setAffiliateNetworks(Array.isArray(nRes.value.data) ? nRes.value.data : []);
        if (dRes.status === 'fulfilled') setDomains(Array.isArray(dRes.value.data) ? dRes.value.data : []);
        setHasFetched(true);
      } catch {
        // silent — palette still useful for navigation
        setHasFetched(true);
      }
    };
    load();
    return () => { cancelled = true; };
  }, [open, hasFetched]);

  const go = useCallback((path) => {
    navigate(path);
    close();
  }, [navigate, close]);

  const goExternal = useCallback((url) => {
    window.open(url, '_blank', 'noopener,noreferrer');
    close();
  }, [close]);

  const campaignsLabel = useMemo(() => t('Campaigns'), [t]);
  const pagesLabel = useMemo(() => t('Landing pages'), [t]);
  const offersLabel = useMemo(() => t('Offers'), [t]);
  const sourcesLabel = useMemo(() => t('Traffic sources'), [t]);
  const networksLabel = useMemo(() => t('Affiliate networks'), [t]);
  const domainsLabel = useMemo(() => t('Domains'), [t]);

  // Pre-compute navigation entries grouped by their `group` key so the
  // palette renders one Command.Group per bucket. The admin bucket is
  // operator-only — same visibleNavItems() gate as the sidebar — so the
  // memo re-runs when the resolved user (is_admin) changes.
  const navEntriesByGroup = useMemo(() => {
    const buckets = new Map();
    for (const entry of visibleNavItems(NAV_ENTRIES, user, { selfHosted })) {
      const g = entry.group || 'navigate';
      if (!buckets.has(g)) buckets.set(g, []);
      buckets.get(g).push(entry);
    }
    return NAV_GROUPS
      .filter((g) => buckets.has(g.key))
      .map((g) => ({ ...g, entries: buckets.get(g.key) }));
  }, [user, selfHosted]);

  // The API docs link depends on the resolved backend URL; keep a memo
  // so we don't recompute on every keystroke.
  const apiDocsUrl = useMemo(
    () => `${BACKEND_URL}/api/public/docs`,
    []
  );

  // Custom scorer — cmdk's default is a loose fuzzy (subsequence) match that
  // ranked irrelevant items above exact ones (e.g. "aurora" surfacing
  // "Traffic sources"). Score on real substring / word-prefix / exact match
  // and return 0 for everything else so non-matches are hidden entirely.
  const cmdkFilter = useCallback((value, search) => {
    const s = (search || '').trim().toLowerCase();
    if (!s) return 1;
    const v = (value || '').toLowerCase();
    if (v === s) return 1;
    if (v.startsWith(s)) return 0.95;
    const words = v.split(/[\s/_-]+/).filter(Boolean);
    if (words.some((w) => w === s)) return 0.9;
    if (words.some((w) => w.startsWith(s))) return 0.8;
    if (v.includes(s)) return 0.5;
    return 0; // hide non-substring matches (kills the fuzzy noise)
  }, []);

  if (typeof document === 'undefined') return null;

  return createPortal(
    <Command.Dialog
      open={open}
      onOpenChange={setOpen}
      label="Global command palette"
      className="xp-cmdk-root"
      shouldFilter
      filter={cmdkFilter}
    >
      <div
        className="xp-cmdk-backdrop"
        onClick={close}
        aria-hidden="true"
      />
      <div className="xp-cmdk-shell" data-testid="command-palette">
        <div className="xp-cmdk-header">
          <Search className="xp-cmdk-search-icon" />
          <Command.Input
            value={query}
            onValueChange={setQuery}
            placeholder={t('Type a command or search…')}
            className="xp-cmdk-input"
            autoFocus
            data-testid="command-palette-input"
          />
          <kbd className="xp-cmdk-kbd">esc</kbd>
        </div>
        <Command.List className="xp-cmdk-list">
          <Command.Empty className="xp-cmdk-empty">
            {t('No results.')}
          </Command.Empty>

          {navEntriesByGroup.map((group) => (
            <Command.Group key={group.key} heading={t(group.label)} className="xp-cmdk-group">
              {group.entries.map((entry) => (
                <Command.Item
                  key={entry.id}
                  value={`${entry.label} ${entry.path} ${group.label}`}
                  onSelect={() => go(entry.path)}
                  className="xp-cmdk-item"
                >
                  <entry.icon className="xp-cmdk-item-icon" />
                  <span className="xp-cmdk-item-label">{t(entry.label)}</span>
                  <span className="xp-cmdk-item-path">{entry.path}</span>
                  <ArrowRight className="xp-cmdk-item-chev" />
                </Command.Item>
              ))}
            </Command.Group>
          ))}

          {campaigns.length > 0 ? (
            <Command.Group heading={campaignsLabel} className="xp-cmdk-group">
              {campaigns.slice(0, 8).map((c) => (
                <Command.Item
                  key={`c-${c.id}`}
                  value={`campaign ${c.name} ${c.description || ''}`}
                  onSelect={() => go(`/campaigns/${c.id}`)}
                  className="xp-cmdk-item"
                >
                  <Megaphone className="xp-cmdk-item-icon" />
                  <span className="xp-cmdk-item-label">{c.name}</span>
                  <span className="xp-cmdk-item-path">/campaigns/{c.id.slice(0, 6)}</span>
                  <ArrowRight className="xp-cmdk-item-chev" />
                </Command.Item>
              ))}
            </Command.Group>
          ) : null}

          {landingPages.length > 0 ? (
            <Command.Group heading={pagesLabel} className="xp-cmdk-group">
              {landingPages.slice(0, 6).map((p) => (
                <Command.Item
                  key={`p-${p.id}`}
                  value={`page ${p.name} ${p.url || ''}`}
                  onSelect={() => go('/landing-pages')}
                  className="xp-cmdk-item"
                >
                  <PanelTop className="xp-cmdk-item-icon" />
                  <span className="xp-cmdk-item-label">{p.name}</span>
                  <span className="xp-cmdk-item-path">/landing-pages</span>
                  <ArrowRight className="xp-cmdk-item-chev" />
                </Command.Item>
              ))}
            </Command.Group>
          ) : null}

          {offers.length > 0 ? (
            <Command.Group heading={offersLabel} className="xp-cmdk-group">
              {offers.slice(0, 6).map((o) => (
                <Command.Item
                  key={`o-${o.id}`}
                  value={`offer ${o.name} ${o.payout || ''}`}
                  onSelect={() => go('/offers')}
                  className="xp-cmdk-item"
                >
                  <Gift className="xp-cmdk-item-icon" />
                  <span className="xp-cmdk-item-label">{o.name}</span>
                  <span className="xp-cmdk-item-path">/offers</span>
                  <ArrowRight className="xp-cmdk-item-chev" />
                </Command.Item>
              ))}
            </Command.Group>
          ) : null}

          {trafficSources.length > 0 ? (
            <Command.Group heading={sourcesLabel} className="xp-cmdk-group">
              {trafficSources.slice(0, 6).map((s) => (
                <Command.Item
                  key={`s-${s.id}`}
                  value={`traffic source ${s.name || ''}`}
                  onSelect={() => go('/traffic-sources')}
                  className="xp-cmdk-item"
                >
                  <Route className="xp-cmdk-item-icon" />
                  <span className="xp-cmdk-item-label">{s.name}</span>
                  <span className="xp-cmdk-item-path">/traffic-sources</span>
                  <ArrowRight className="xp-cmdk-item-chev" />
                </Command.Item>
              ))}
            </Command.Group>
          ) : null}

          {affiliateNetworks.length > 0 ? (
            <Command.Group heading={networksLabel} className="xp-cmdk-group">
              {affiliateNetworks.slice(0, 6).map((n) => (
                <Command.Item
                  key={`n-${n.id}`}
                  value={`affiliate network ${n.name || ''}`}
                  onSelect={() => go('/affiliate-networks')}
                  className="xp-cmdk-item"
                >
                  <Plug className="xp-cmdk-item-icon" />
                  <span className="xp-cmdk-item-label">{n.name}</span>
                  <span className="xp-cmdk-item-path">/affiliate-networks</span>
                  <ArrowRight className="xp-cmdk-item-chev" />
                </Command.Item>
              ))}
            </Command.Group>
          ) : null}

          {domains.length > 0 ? (
            <Command.Group heading={domainsLabel} className="xp-cmdk-group">
              {domains.slice(0, 6).map((d) => (
                <Command.Item
                  key={`d-${d.id}`}
                  value={`domain ${d.hostname || ''}`}
                  onSelect={() => go('/domains')}
                  className="xp-cmdk-item"
                >
                  <Globe2 className="xp-cmdk-item-icon" />
                  <span className="xp-cmdk-item-label">{d.hostname}</span>
                  <span className="xp-cmdk-item-path">/domains</span>
                  <ArrowRight className="xp-cmdk-item-chev" />
                </Command.Item>
              ))}
            </Command.Group>
          ) : null}

          <Command.Group heading={t('Quick actions')} className="xp-cmdk-group">
            <Command.Item
              value="create campaign new"
              onSelect={() => go('/campaigns?new=1')}
              className="xp-cmdk-item"
            >
              <Plus className="xp-cmdk-item-icon" />
              <span className="xp-cmdk-item-label">{t('Create new campaign')}</span>
              <span className="xp-cmdk-item-path">/campaigns</span>
              <ArrowRight className="xp-cmdk-item-chev" />
            </Command.Item>
            <Command.Item
              value="create landing page new"
              onSelect={() => go('/landing-pages?new=1')}
              className="xp-cmdk-item"
            >
              <Plus className="xp-cmdk-item-icon" />
              <span className="xp-cmdk-item-label">{t('Create new landing page')}</span>
              <span className="xp-cmdk-item-path">/landing-pages</span>
              <ArrowRight className="xp-cmdk-item-chev" />
            </Command.Item>
            <Command.Item
              value="create offer new"
              onSelect={() => go('/offers?new=1')}
              className="xp-cmdk-item"
            >
              <Plus className="xp-cmdk-item-icon" />
              <span className="xp-cmdk-item-label">{t('Create new offer')}</span>
              <span className="xp-cmdk-item-path">/offers</span>
              <ArrowRight className="xp-cmdk-item-chev" />
            </Command.Item>
          </Command.Group>

          <Command.Group heading={t('Resources')} className="xp-cmdk-group">
            {EXTERNAL_LINKS.map((link) => {
              const url = link.url || (link.id === 'ext-api-docs' ? apiDocsUrl : '');
              if (!url) return null;
              return (
                <Command.Item
                  key={link.id}
                  value={`resource ${link.label} ${url}`}
                  onSelect={() => goExternal(url)}
                  className="xp-cmdk-item"
                >
                  <link.icon className="xp-cmdk-item-icon" />
                  <span className="xp-cmdk-item-label">{t(link.label)}</span>
                  <span className="xp-cmdk-item-path">{url.replace(/^https?:\/\//, '')}</span>
                  <ExternalLink className="xp-cmdk-item-chev" />
                </Command.Item>
              );
            })}
          </Command.Group>

        </Command.List>
        <div className="xp-cmdk-footer">
          <span className="xp-cmdk-footer-hint">
            <kbd>↑</kbd> <kbd>↓</kbd> {t('to navigate')}
          </span>
          <span className="xp-cmdk-footer-hint">
            <kbd>↵</kbd> {t('to select')}
          </span>
          <span className="xp-cmdk-footer-hint">
            <kbd>esc</kbd> {t('to close')}
          </span>
        </div>
      </div>
    </Command.Dialog>,
    document.body
  );
}
